import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { remindersAPI } from "../services/api";
import { useI18n } from "../i18n";
import { enablePush, pushState, syncPush } from "../utils/push";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * The owner's reminders: a notification at a chosen time on chosen days — what
 * to bring from the supplier (the shopping list from the Stock screen), or
 * whatever else is worth being told. They are pushed to the devices this
 * person has allowed; the browser's own prompt is only raised from the button
 * on this screen, after they have read what it is for.
 */
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
// A week starting on Monday reads naturally for a shop
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAYS = [1, 2, 3, 4, 5];

/**
 * A new reminder. With a warehouse, the errand people actually make is "what do I
 * take from home to the shop", so that is ticked; the shopping list (what to buy)
 * is for the trip to the supplier.
 */
const blank = ({ refill = false, buy = false } = {}) => ({
  title: "",
  message: "",
  include_shopping_list: buy,
  include_refill_list: refill,
  remind_at: "09:00",
  weekdays: WEEKDAYS,
});

/** "Mon", "lun.", "الاثنين" — a day's short name in the device's language. */
const dayName = (day, locale) =>
  new Date(Date.UTC(2023, 0, 1 + day)).toLocaleDateString(locale, { weekday: "short", timeZone: "UTC" });

/**
 * Asks whether this device receives reminders, and disappears once it does.
 *
 * A device that already allowed notifications is re-registered silently (no
 * prompt) in case the server lost track of it, then the card is not shown at
 * all. Only a device that has not said yes sees the button — the browser's
 * own prompt is only ever raised from it.
 */
const DeviceCard = () => {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [turnedOn, setTurnedOn] = useState(false);
  // This device is set up: nothing to ask, nothing to show
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const next = await pushState();
      if (cancelled) return;

      if (next.available && !next.reason && (await syncPush()).ok) {
        if (!cancelled) setReady(true);
      }

      if (!cancelled) setState(next);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const accept = async () => {
    setBusy(true);
    setFailure(null);

    try {
      const result = await enablePush();
      if (result.ok) setTurnedOn(true);
      else setFailure(result.reason);
    } catch {
      setFailure("failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state || ready) return null;

  // Just turned on here: say so once, then it is gone on the next visit
  if (turnedOn) {
    return (
      <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm">
        {t("reminders.device.on")}
      </div>
    );
  }

  const reason = failure || state.reason;

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-s-4 border-primary-500">
      <h2 className="font-bold text-gray-800">{t("reminders.device.title")}</h2>

      {!state.available ? (
        <p className="mt-1 text-gray-600">
          {t(reason === "unsupported" ? "reminders.device.unsupported" : "reminders.device.unavailable")}
        </p>
      ) : reason === "ios-needs-install" ? (
        <p className="mt-1 text-gray-600">{t("reminders.device.ios")}</p>
      ) : (
        <>
          <p className="mt-1 text-gray-600">{t("reminders.device.body")}</p>
          {(reason === "denied" || reason === "failed") && (
            <p className="mt-2 text-sm text-red-600">
              {t(reason === "denied" ? "reminders.device.denied" : "reminders.device.failed")}
            </p>
          )}
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            className="mt-4 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? "…" : t("reminders.device.turnOn")}
          </button>
          <p className="mt-2 text-xs text-gray-500">{t("reminders.device.confirmHint")}</p>
        </>
      )}
    </div>
  );
};

const Reminders = () => {
  const { t, locale } = useI18n();
  const { shop } = useMembership();
  const warehouse = Boolean(shop?.warehouse_enabled);
  const [params, setParams] = useSearchParams();
  const [reminders, setReminders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await remindersAPI.list();
      setReminders(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("reminders.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // "Remind me" from the shopping list arrives with the list already ticked
  useEffect(() => {
    if (params.get("new") === "list") {
      setEditing(blank({ refill: warehouse, buy: true }));
      setParams({}, { replace: true });
    }
  }, [params, setParams, warehouse]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      title: editing.title,
      message: editing.message,
      include_shopping_list: editing.include_shopping_list,
      include_refill_list: editing.include_refill_list,
      remind_at: editing.remind_at,
      weekdays: editing.weekdays,
    };

    try {
      if (editing.id) await remindersAPI.update(editing.id, payload);
      else await remindersAPI.create(payload);

      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("reminders.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (reminder) => {
    try {
      await remindersAPI.update(reminder.id, { is_active: !reminder.is_active });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("reminders.saveFailed"));
    }
  };

  const remove = async (reminder) => {
    if (!window.confirm(t("reminders.deleteConfirm"))) return;

    try {
      await remindersAPI.remove(reminder.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("reminders.saveFailed"));
    }
  };

  const test = async (reminder) => {
    setNotice("");

    try {
      const { data } = await remindersAPI.sendNow(reminder.id);
      setNotice(
        data.sent > 0
          ? t("reminders.sentNow", { count: data.sent })
          : data.skipped
            ? t("reminders.nothingToSay")
            : t("reminders.noDevice"),
      );
    } catch (err) {
      setError(err.response?.data?.message || t("reminders.saveFailed"));
    }
  };

  const toggleDay = (day) =>
    setEditing((current) => ({
      ...current,
      weekdays: current.weekdays.includes(day)
        ? current.weekdays.filter((d) => d !== day)
        : [...current.weekdays, day],
    }));

  const summary = (reminder) => {
    const days =
      reminder.weekdays.length === 7
        ? t("reminders.everyDay")
        : WEEK_ORDER.filter((day) => reminder.weekdays.includes(day))
            .map((day) => dayName(day, locale))
            .join(", ");

    return `${days} · ${reminder.remind_at}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  const saveDisabled =
    busy ||
    editing?.weekdays.length === 0 ||
    (!editing?.include_shopping_list &&
      !editing?.include_refill_list &&
      !editing?.message.trim() &&
      !editing?.title.trim());

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t("reminders.title")}</h1>
          <p className="text-gray-600 mt-1 max-w-2xl">{t("reminders.subtitle")}</p>
        </div>
        <button
          onClick={() => setEditing(editing ? null : blank({ refill: warehouse, buy: !warehouse }))}
          className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
        >
          {editing ? t("common.cancel") : t("reminders.new")}
        </button>
      </div>

      <DeviceCard />

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("reminders.form.title")}</span>
            <input
              type="text"
              maxLength={120}
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder={t("reminders.form.titlePlaceholder")}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("reminders.form.message")}</span>
            <textarea
              rows={2}
              maxLength={500}
              value={editing.message}
              onChange={(e) => setEditing({ ...editing, message: e.target.value })}
              placeholder={t("reminders.form.messagePlaceholder")}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </label>

          {warehouse && (
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={editing.include_refill_list}
                onChange={(e) => setEditing({ ...editing, include_refill_list: e.target.checked })}
                className="mt-1 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700">{t("reminders.form.includeRefill")}</span>
                <span className="block text-xs text-gray-500">{t("reminders.form.includeRefillHint")}</span>
              </span>
            </label>
          )}

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={editing.include_shopping_list}
              onChange={(e) => setEditing({ ...editing, include_shopping_list: e.target.checked })}
              className="mt-1 h-4 w-4"
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">{t("reminders.form.includeList")}</span>
              <span className="block text-xs text-gray-500">{t("reminders.form.includeListHint")}</span>
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-[auto,1fr] items-start">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("reminders.form.time")}</span>
              <input
                type="time"
                required
                dir="ltr"
                value={editing.remind_at}
                onChange={(e) => setEditing({ ...editing, remind_at: e.target.value })}
                className="mt-1 block px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <fieldset>
              <legend className="text-sm font-medium text-gray-700">{t("reminders.form.days")}</legend>
              <div className="mt-1 flex flex-wrap gap-2">
                {WEEK_ORDER.map((day) => (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={editing.weekdays.includes(day)}
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                      editing.weekdays.includes(day)
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {dayName(day, locale)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, weekdays: editing.weekdays.length === 7 ? WEEKDAYS : ALL_DAYS })}
                  className="px-3 py-2 rounded-lg text-sm text-primary-600 hover:underline"
                >
                  {editing.weekdays.length === 7 ? t("reminders.form.workDays") : t("reminders.form.everyDay")}
                </button>
              </div>
            </fieldset>
          </div>

          <p className="text-xs text-gray-500">{t("reminders.form.timezoneHint")}</p>

          <button
            type="submit"
            disabled={saveDisabled}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("reminders.form.save")}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {reminders.length === 0 ? (
          <EmptyState screen="reminders" message={t("reminders.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {reminders.map((reminder) => (
              <li key={reminder.id} className="py-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
                    ⏰ {reminder.title || t("reminders.untitled")}
                    {!reminder.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {t("reminders.paused")}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600" dir="auto">
                    {summary(reminder)}
                  </p>
                  {reminder.message && <p className="text-sm text-gray-500 mt-0.5">{reminder.message}</p>}
                  {reminder.include_refill_list && (
                    <p className="text-sm text-primary-700 mt-0.5">🏬 {t("reminders.withRefill")}</p>
                  )}
                  {reminder.include_shopping_list && (
                    <p className="text-sm text-primary-700 mt-0.5">🛒 {t("reminders.withList")}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => test(reminder)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm">
                    {t("reminders.sendNow")}
                  </button>
                  <button onClick={() => toggle(reminder)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm">
                    {reminder.is_active ? t("reminders.pause") : t("reminders.resume")}
                  </button>
                  <button
                    onClick={() =>
                      setEditing({
                        id: reminder.id,
                        title: reminder.title || "",
                        message: reminder.message || "",
                        include_shopping_list: reminder.include_shopping_list,
                        include_refill_list: reminder.include_refill_list,
                        remind_at: reminder.remind_at,
                        weekdays: reminder.weekdays,
                      })
                    }
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    onClick={() => remove(reminder)}
                    className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm"
                  >
                    {t("reminders.delete")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Reminders;
